// Notifications Bell Component with badge
import { useState, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationsBell({ language = 'fr' }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const lastSeenRef = useRef(localStorage.getItem('lastNotificationSeen') || '0');
  
  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);
  
  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications/history`, { withCredentials: true });
      const notifs = response.data || [];
      setNotifications(notifs);
      
      // Count notifications newer than lastSeen
      const lastSeen = parseInt(lastSeenRef.current);
      const newCount = notifs.filter(n => new Date(n.timestamp).getTime() > lastSeen && !n.read).length;
      setUnreadCount(newCount);
    } catch (error) {
      // Generate mock notifications with fixed timestamps (not relative to now)
      // Use localStorage to track read status for mock notifications
      const readNotifIds = JSON.parse(localStorage.getItem('readNotificationIds') || '[]');
      
      const mockNotifs = [
        {
          id: 'mock-1',
          title: language === 'fr' ? '🎯 Objectif atteint !' : '🎯 Goal reached!',
          body: language === 'fr' ? 'Vous avez atteint 10 000 pas aujourd\'hui' : 'You reached 10,000 steps today',
          timestamp: '2025-01-08T10:00:00.000Z',
          read: readNotifIds.includes('mock-1')
        },
        {
          id: 'mock-2',
          title: language === 'fr' ? '💪 Rappel entraînement' : '💪 Workout reminder',
          body: language === 'fr' ? 'N\'oubliez pas votre séance de 19h' : 'Don\'t forget your 7pm session',
          timestamp: '2025-01-07T18:00:00.000Z',
          read: readNotifIds.includes('mock-2')
        },
        {
          id: 'mock-3',
          title: language === 'fr' ? '🔥 Série en cours !' : '🔥 Streak active!',
          body: language === 'fr' ? '5 jours consécutifs ! Continuez !' : '5 consecutive days! Keep going!',
          timestamp: '2025-01-06T09:00:00.000Z',
          read: readNotifIds.includes('mock-3')
        }
      ];
      setNotifications(mockNotifs);
      
      // Count only unread notifications
      const newCount = mockNotifs.filter(n => !n.read).length;
      setUnreadCount(newCount);
    }
  };
  
  const markAsRead = async () => {
    // Save current time as last seen
    const now = Date.now().toString();
    localStorage.setItem('lastNotificationSeen', now);
    lastSeenRef.current = now;
    setUnreadCount(0);
    
    // Get all notification IDs before updating state
    const allIds = notifications.map(n => n.id);
    
    // Save read notification IDs to localStorage for mock notifications persistence
    localStorage.setItem('readNotificationIds', JSON.stringify(allIds));
    
    // Update notifications to show as read
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    
    try {
      await axios.post(`${API}/notifications/mark-read`, {}, { withCredentials: true });
    } catch (error) {
      // Silent fail - localStorage already updated
    }
  };
  
  const handleOpen = (isOpen) => {
    setOpen(isOpen);
    if (isOpen && unreadCount > 0) {
      // Mark as read when user opens the sheet
      markAsRead();
    }
  };
  
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 3600000) {
      const mins = Math.floor(diff / 60000);
      return language === 'fr' ? `Il y a ${mins} min` : `${mins}m ago`;
    }
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return language === 'fr' ? `Il y a ${hours}h` : `${hours}h ago`;
    }
    const days = Math.floor(diff / 86400000);
    return language === 'fr' ? `Il y a ${days}j` : `${days}d ago`;
  };
  
  return (
    <Sheet open={open} onOpenChange={handleOpen}>
      <SheetTrigger asChild>
        <button className="relative p-2 rounded-full hover:bg-muted/50 transition-colors">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center animate-pulse">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </SheetTrigger>
      
      <SheetContent side="right" className="w-80">
        <SheetHeader>
          <SheetTitle>
            {language === 'fr' ? 'Notifications' : 'Notifications'}
          </SheetTitle>
        </SheetHeader>
        
        <ScrollArea className="h-[calc(100vh-100px)] mt-4">
          {notifications.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Bell className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p className="text-sm">
                {language === 'fr' ? 'Aucune notification' : 'No notifications'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map((notif) => (
                <div 
                  key={notif.id}
                  className={`p-3 rounded-lg border transition-colors ${
                    notif.read ? 'bg-muted/20' : 'bg-primary/5 border-primary/20'
                  }`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <p className="font-medium text-sm">{notif.title}</p>
                    {!notif.read && (
                      <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{notif.body}</p>
                  <p className="text-[10px] text-muted-foreground mt-2">
                    {formatTime(notif.timestamp)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
